import { Spine } from "@esotericsoftware/spine-pixi-v8";
import * as PIXI from "pixi.js";
import { useCallback, useRef, useEffect } from "preact/hooks";
import { GameState, GameStateEnum } from "../../../shared/types/global.types";
import { useSkinStore, resolveSpineAsset } from "../../../store/skinStore";
import { applyParallaxStep, startParallaxAnimation } from "./useSpineHelpers";

interface UseSpineBackgroundProps {
  multiplier: number;
  gameState?: GameState;
}

export function useSpineBackground({
  multiplier,
  gameState,
}: UseSpineBackgroundProps) {
  const { getSkin } = useSkinStore();
  const backgroundSpineRef = useRef<Spine | null>(null);
  const canvasHRef = useRef<number>(0);
  const basePositionYRef = useRef<number | null>(null);
  const baseBoundsRef = useRef<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const resizeTimerRef = useRef<number | null>(null);
  const pendingSizeRef = useRef<{ w: number; h: number } | null>(null);
  const interpolatedMultiplierRef = useRef<number>(1);
  const spineHeightRef = useRef<number>(0);
  const prevMultiplierRef = useRef<number>(1);
  const targetMultiplierRef = useRef<number>(1);
  const tStartRef = useRef<number | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const isAnimatingRef = useRef<boolean>(false);

  const parallaxRefs = {
    spineRef: backgroundSpineRef,
    basePositionYRef,
    interpolatedMultiplierRef,
    prevMultiplierRef,
    targetMultiplierRef,
    tStartRef,
    animationFrameRef,
    isAnimatingRef,
  };

  const fitAndPositionBg = useCallback(
    (spine: Spine, _w: number, h: number) => {
      if (!spine) return;

      const b = spine.getLocalBounds();

      spine.position.x = 0;
      spine.position.y = 0;

      spineHeightRef.current = b.height;

      canvasHRef.current = h;
      basePositionYRef.current = 0;
    },
    []
  );

  const applyParallax = useCallback(() => {
    const shouldContinue = applyParallaxStep(parallaxRefs);
    if (shouldContinue) {
      animationFrameRef.current = requestAnimationFrame(applyParallax);
    }
  }, []);

  const startAnimation = useCallback(
    (targetMultiplier: number) => {
      startParallaxAnimation(parallaxRefs, applyParallax, targetMultiplier);
    },
    [applyParallax]
  );

  const createBackground = useCallback(
    async (parent: PIXI.Container, w: number, h: number) => {
      try {
        const skin = getSkin("pioneer");
        const resolved = resolveSpineAsset(skin.bg);
        const skel = resolved.skeleton;
        const atl = resolved.atlas;
        await PIXI.Assets.load([skel, atl]);
        const backgroundSpine = Spine.from({
          skeleton: skel,
          atlas: atl,
          scale: 1,
          autoUpdate: true,
        });
        backgroundSpine.roundPixels = false;
        backgroundSpine.zIndex = 0.5;

        backgroundSpineRef.current = backgroundSpine;
        parent.addChild(backgroundSpine);

        try {
          // Базовый фон — самый дальний слой (ниже космоса и лестницы)
          parent.setChildIndex(backgroundSpine, 0);
        } catch (e) {
          console.warn("[BACKGROUND DEBUG] setChildIndex error", e);
        }

        canvasHRef.current = h;
        basePositionYRef.current = null;
        fitAndPositionBg(backgroundSpine, w, h);

        backgroundSpine.state.setAnimation(0, "back", true);

        return backgroundSpine;
      } catch (error) {
        console.error("useSpineBackground: ошибка создания фона:", error);
        throw error;
      }
    },
    [fitAndPositionBg, getSkin]
  );
  const checkBackgroundHealth = useCallback(() => {
    const backgroundSpine = backgroundSpineRef.current;
    if (backgroundSpine && backgroundSpine.state) {
      const [bgTrack] = backgroundSpine.state.tracks;
      if (bgTrack && bgTrack.animation) {
        backgroundSpine.state.clearTracks();
        backgroundSpine.skeleton.setToSetupPose();
        backgroundSpine.state.apply(backgroundSpine.skeleton);
      }
    }
  }, []);

  const resizeBackground = useCallback(
    (w: number, h: number) => {
      // Обновляем spine бэкграунд
      const backgroundSpine = backgroundSpineRef.current;
      if (!backgroundSpine) return;

      // Обновляем текущую высоту канвы и пересчитываем позиционирование
      canvasHRef.current = h;
      // Сбрасываем базовую позицию, чтобы fitAndPositionBg вычислил её заново
      basePositionYRef.current = null;
      fitAndPositionBg(backgroundSpine, w, h);
    },
    [fitAndPositionBg]
  );

  const destroyBackground = useCallback(() => {
    if (resizeTimerRef.current !== null) {
      clearTimeout(resizeTimerRef.current);
      resizeTimerRef.current = null;
    }
    pendingSizeRef.current = null;

    // Удаляем spine бэкграунд
    const backgroundSpine = backgroundSpineRef.current;
    if (backgroundSpine) {
      try {
        backgroundSpine.destroy();
      } catch {
        // ignore
      }
      backgroundSpineRef.current = null;
    }

    baseBoundsRef.current = null;
    basePositionYRef.current = null;
    canvasHRef.current = 0;
  }, []);

  useEffect(() => {
    if (gameState === GameStateEnum.COUNTDOWN) {
      const spine = backgroundSpineRef.current;
      if (spine) {
        spine.position.x = 0;
        spine.position.y = 0;
        basePositionYRef.current = 0;

        interpolatedMultiplierRef.current = 1;
        prevMultiplierRef.current = 1;
        targetMultiplierRef.current = 1;

        if (animationFrameRef.current !== null) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
        isAnimatingRef.current = false;

        try {
          spine.state.setAnimation(0, "back", true);
        } catch (error) {
          console.warn(
            "useSpineBackground: не удалось перезапустить анимацию back",
            error
          );
        }
      }
    }
  }, [gameState]);

  useEffect(() => {
    if (gameState === GameStateEnum.COUNTDOWN) return;
    startAnimation(multiplier);
  }, [multiplier, startAnimation, gameState]);

  useEffect(() => {
    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  return {
    backgroundSpineRef,
    createBackground,
    checkBackgroundHealth,
    resizeBackground,
    destroyBackground,
    fitAndPositionBg,
  };
}
